import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import RequireAdmin from './components/RequireAdmin.jsx';
import RequireSuperAdmin from './components/RequireSuperAdmin.jsx';
import RequireResident from './components/RequireResident.jsx';
import RequireBrainHealthUser from './components/RequireBrainHealthUser.jsx';
import Home from './pages/Home.jsx';
import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import Welcome from './pages/Welcome.jsx';
import Account from './pages/Account.jsx';
import Assessment from './pages/Assessment.jsx';
import AssessmentResults from './pages/AssessmentResults.jsx';
import Goals from './pages/Goals.jsx';
import Dashboard from './pages/Dashboard.jsx';
import AdminLogin from './pages/AdminLogin.jsx';
import AdminCommunities from './pages/AdminCommunities.jsx';
import AdminResidents from './pages/AdminResidents.jsx';
import AdminAssessment from './pages/AdminAssessment.jsx';
import AdminCommunityPicker from './pages/AdminCommunityPicker.jsx';
import AdminNoAccess from './pages/AdminNoAccess.jsx';
import GamesCatalog from './pages/GamesCatalog.jsx';
import NotFound from './pages/NotFound.jsx';
// PREVIEW — remove when no longer needed.
import PreviewContextProvider from './components/PreviewContextProvider.jsx';

// Routes.
// - RequireResident: localStorage resident session (community only).
//   Still exported and available, but unused on Brain Health pages
//   now that the dashboard + onboarding flow accept all identity
//   types via RequireBrainHealthUser. Kept around in case future
//   community-only features need it.
// - RequireBrainHealthUser: any active context — community resident,
//   entitled individual subscriber, or device user. Used for the
//   unified dashboard and onboarding flow.
// - RequireAdmin: signed-in via Firebase Auth AND has at least one
//   admin role (super or community).
// - RequireSuperAdmin: signed-in AND is fpeyerl@quiltt.com specifically.
//
// Phase 2C completed the migration of Dashboard, Assessment, Goals,
// and AssessmentResults to read from useCurrentContext, which means
// /resident and /onboarding/* now work end-to-end for both community
// residents and individual subscribers. The same components, the same
// URLs, the same UI — only the RTDB destination differs (under
// /accounts/ vs /users/).
//
// Phase 3 added the /welcome.html route for Hexnode-managed kiosk
// tablets. This route lives OUTSIDE the Layout wrapper because the
// tablet welcome screen is a full-screen kiosk experience that
// shouldn't show the masthead. The URL path is /welcome.html (with
// the .html suffix) deliberately — it matches the Hexnode policy URL
// that's already provisioned to devices, and avoids any cleanUrls
// SPA-rewrite ambiguity.
export default function App() {
  return (
    <Routes>
      {/* Device-bundled tablet kiosk welcome screen. Outside Layout
          because it's a full-screen kiosk experience. Reachable at
          /welcome.html?did=<udid> — Hexnode substitutes %udid% into
          the policy URL before the request leaves the device. */}
      <Route path="/welcome.html" element={<Welcome />} />

      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />

        {/* Individual self-serve signup (Phase 2 Brain Health).
            Public — visitors land here to sign up. Returning users
            also use this page for sign-in, since magic-link auth
            doesn't distinguish. */}
        <Route path="/signup" element={<Signup />} />

        {/* Onboarding flow — assessment, results, goals.
            Open to any active brain health identity. Pages source
            identity from useCurrentContext and write to whichever
            RTDB root the user lives under. */}
        <Route
          path="/onboarding/assessment"
          element={<RequireBrainHealthUser><Assessment /></RequireBrainHealthUser>}
        />
        <Route
          path="/onboarding/results"
          element={<RequireBrainHealthUser><AssessmentResults /></RequireBrainHealthUser>}
        />
        <Route
          path="/onboarding/goals"
          element={<RequireBrainHealthUser><Goals /></RequireBrainHealthUser>}
        />

        {/* PREVIEW — remove when no longer needed.
            Unauthenticated walkthrough of the full onboarding flow
            (assessment → results → goals) with a mock identity and
            NO Firebase writes. Inside Layout for the masthead, but
            deliberately OUTSIDE RequireBrainHealthUser so it needs no
            sign-in. Share /assessment/preview to demo the flow. */}
        <Route
          path="/assessment/preview"
          element={<PreviewContextProvider><Assessment /></PreviewContextProvider>}
        />
        <Route
          path="/assessment/preview/results"
          element={<PreviewContextProvider><AssessmentResults /></PreviewContextProvider>}
        />
        <Route
          path="/assessment/preview/goals"
          element={<PreviewContextProvider><Goals /></PreviewContextProvider>}
        />

        {/* Unified Brain Health dashboard. Open to any active context.
            Dashboard component reads from useCurrentContext and writes
            session data to the right base path regardless of identity
            type. The URL name "/resident" is historical; the page
            serves community residents AND individual subscribers. */}
        <Route
          path="/resident"
          element={<RequireBrainHealthUser><Dashboard /></RequireBrainHealthUser>}
        />

        {/* Individual subscriber's home, parallel to /resident.
            Uses the broader guard since any active context might
            land here (the page itself routes residents and devices
            back to their proper dashboards). */}
        <Route
          path="/account"
          element={
            <RequireBrainHealthUser>
              <Account />
            </RequireBrainHealthUser>
          }
        />

        {/* Admin sign-in is public (it's how you sign in) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/no-access" element={<AdminNoAccess />} />

        {/* /admin redirects to the user's natural landing page.
            Without auth context here, we just bounce to login, which
            in turn routes correctly after sign-in. */}
        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />

        {/* Multi-community picker — any signed-in admin can see this */}
        <Route
          path="/admin/picker"
          element={<RequireAdmin><AdminCommunityPicker /></RequireAdmin>}
        />

        {/* Super-admin only */}
        <Route
          path="/admin/communities"
          element={<RequireSuperAdmin><AdminCommunities /></RequireSuperAdmin>}
        />
        <Route
          path="/admin/catalog"
          element={<RequireSuperAdmin><GamesCatalog /></RequireSuperAdmin>}
        />

        {/* Community-scoped admin pages */}
        <Route
          path="/admin/communities/:communityCode/residents"
          element={<RequireAdmin><AdminResidents /></RequireAdmin>}
        />
        <Route
          path="/admin/residents/:residentId/assessment"
          element={<RequireAdmin><AdminAssessment /></RequireAdmin>}
        />

        <Route
          path="/admin/residents"
          element={<Navigate to="/admin" replace />}
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
